import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, ChevronUp, ChevronDown, X } from 'lucide-react';

const ITEM_TYPES = [
  { value: '', label: 'None' },
  { value: 'basic', label: 'Basic' },
  { value: 'advanced', label: 'Advanced' },
];

const TAG_COLORS = [
  { value: 'red', label: 'Red' },
  { value: 'gold', label: 'Gold' },
];

const newKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `k_${Math.random().toString(36).slice(2)}_${Date.now()}`);

const emptyDraft = { id: null, category: '', description: '', items: [], visible: true };

const ItemsEditor = ({ items, onChange }) => {
  const update = (idx, patch) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], ...patch };
    onChange(copy);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    onChange(copy);
  };
  const add = () => onChange([...items, { _key: newKey(), service: '', price: '', type: '', tag: '', tag_color: 'red' }]);

  return (
    <div className="space-y-2">
      {items.length === 0 && (
        <p className="text-xs text-gray-500">No line items yet. Click &ldquo;Add item&rdquo; below.</p>
      )}
      {items.map((it, idx) => (
        <div key={it._key || `idx-${idx}`} data-testid={`item-row-${idx}`} className="space-y-1 p-2 rounded bg-slate-950/40 border border-slate-800">
          <div className="grid grid-cols-12 gap-2 items-center">
            <Input
              data-testid={`item-service-${idx}`}
              value={it.service}
              onChange={(e) => update(idx, { service: e.target.value })}
              placeholder="Service name"
              className="col-span-6 bg-slate-800 border-slate-700 text-white"
            />
            <Input
              data-testid={`item-price-${idx}`}
              value={it.price}
              onChange={(e) => update(idx, { price: e.target.value })}
              placeholder="$45 or Quote"
              className="col-span-2 bg-slate-800 border-slate-700 text-white"
            />
            <select
              data-testid={`item-type-${idx}`}
              value={it.type || ''}
              onChange={(e) => update(idx, { type: e.target.value || null })}
              className="col-span-2 bg-slate-800 border-slate-700 text-white border rounded-md h-10 px-2 text-sm"
            >
              {ITEM_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <div className="col-span-2 flex items-center gap-1 justify-end">
              <Button type="button" size="icon" variant="outline" onClick={() => move(idx, -1)} disabled={idx === 0} className="border-slate-700 text-white hover:bg-slate-800 h-8 w-8">
                <ChevronUp className="w-3 h-3" />
              </Button>
              <Button type="button" size="icon" variant="outline" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="border-slate-700 text-white hover:bg-slate-800 h-8 w-8">
                <ChevronDown className="w-3 h-3" />
              </Button>
              <Button type="button" size="icon" variant="outline" onClick={() => remove(idx)} className="border-red-900/60 text-red-400 hover:bg-red-950/40 h-8 w-8">
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-1 text-[10px] text-gray-500 uppercase tracking-wider">Tag</div>
            <Input
              data-testid={`item-tag-${idx}`}
              value={it.tag || ''}
              onChange={(e) => update(idx, { tag: e.target.value })}
              placeholder="Optional — e.g. NEW, Limited Time"
              className="col-span-7 bg-slate-800 border-slate-700 text-white h-8 text-sm"
            />
            <select
              data-testid={`item-tag-color-${idx}`}
              value={it.tag_color || 'red'}
              onChange={(e) => update(idx, { tag_color: e.target.value })}
              disabled={!it.tag}
              className="col-span-2 bg-slate-800 border-slate-700 text-white border rounded-md h-8 px-2 text-xs disabled:opacity-50"
            >
              {TAG_COLORS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            {it.tag ? (
              <span className={`col-span-2 justify-self-end text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${it.tag_color === 'gold' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-600/25 text-red-400'}`}>
                {it.tag}
              </span>
            ) : (
              <span className="col-span-2" />
            )}
          </div>
        </div>
      ))}
      <Button type="button" data-testid="item-add" onClick={add} variant="outline" className="border-slate-700 text-white hover:bg-slate-800">
        <Plus className="w-4 h-4 mr-2" /> Add item
      </Button>
    </div>
  );
};

export default ItemsEditor;
export { newKey };
